import React, {
  ComponentType,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FilterPanel,
  FilterSection,
  FilterField,
  FilterFieldButton,
} from "../Filters/common/FilterPanel.tsx";
import { Filter, MapPin, Search, ShieldCheck, Sparkles, Star, Tags, X } from "lucide-react";
import styles from "./PeopleSearchFilters.module.scss";
import { PeopleSearchTypeOption } from "../../types/peopleSearch.ts";

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>;

type SectionKey =
  | "type"
  | "skills"
  | "categories"
  | "jurisdictions"
  | "verified"
  | "type-mobile"
  | "skills-mobile"
  | "categories-mobile"
  | "jurisdictions-mobile"
  | "verified-mobile";

type SkillOption = {
  id: number;
  name: string;
  users?: number;
};

type JurisdictionOption = {
  id: number;
  name: string;
  users?: number;
};

type CategoryOption = {
  id: number;
  name: string;
  users?: number;
};

type NormalizedSkill = SkillOption & { normalized: string };

type PeopleSearchFiltersProps = {
  filterCount: number;
  totalCount: number;
  type: PeopleSearchTypeOption;
  popularSkills: SkillOption[];
  allSkills: SkillOption[];
  categories: CategoryOption[];
  jurisdictions: JurisdictionOption[];
  selectedSkillIds: number[];
  selectedCategoryIds: number[];
  selectedJurisdictionIds: number[];
  allCountrySelected: boolean;
  allCountryUsers?: number;
  validatedExplore?: { true: number; false: number };
  validatedApplied?: { true: number; false: number };
  typesExplore?: { person: number; company: number; workers: number; total: number };
  featuredCount?: number;
  jurisdictionsLoaded: boolean;
  verificationFilter: "ALL" | "TRUE" | "FALSE";
  featured?: boolean;
  interpretSkillLabels?: Array<{ id: number; name: string }>;
  interpretJurisdictionLabels?: Array<{ id: number; name: string }>;
  onTypeChange: (type: PeopleSearchTypeOption) => void;
  onSkillToggle: (id: number) => void;
  onCategoryToggle: (id: number) => void;
  onJurisdictionToggle: (id: number) => void;
  onToggleAllCountry: () => void;
  onVerificationChange: (value: "ALL" | "TRUE" | "FALSE") => void;
  onToggleFeatured?: () => void;
  onClearFilters: () => void;
  onResetType?: () => void;
  nameValue?: string;
  onSearchClear?: () => void;
};

const typeOptions: Array<{ value: PeopleSearchTypeOption; label: string }> = [
  { value: "ALL", label: "Todos" },
  { value: "FEATURED", label: "Destacados" },
  { value: "PROFESSIONAL", label: "Profesionales" },
  { value: "COMPANY", label: "Empresas" },
  { value: "WORKER", label: "Trabajadores" },
];

const PeopleSearchFilters = ({
  filterCount,
  totalCount,
  type,
  popularSkills,
  allSkills,
  categories,
  jurisdictions,
  selectedSkillIds,
  selectedCategoryIds,
  selectedJurisdictionIds,
  allCountrySelected,
  allCountryUsers,
  validatedExplore,
  validatedApplied,
  typesExplore,
  featuredCount,
  jurisdictionsLoaded,
  verificationFilter,
  featured = false,
  interpretSkillLabels = [],
  interpretJurisdictionLabels = [],
  onTypeChange,
  onSkillToggle,
  onCategoryToggle,
  onJurisdictionToggle,
  onToggleAllCountry,
  onVerificationChange,
  onToggleFeatured,
  onClearFilters,
  onResetType,
  nameValue,
  onSearchClear,
}: PeopleSearchFiltersProps) => {
  const [activeSection, setActiveSection] = useState<SectionKey | null>(null);
  const [activeSkillView, setActiveSkillView] = useState<"top" | "all" | "alphabetical">("top");
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileRef = useRef<HTMLDivElement | null>(null);

  const appliedFiltersCount = filterCount + (verificationFilter !== "ALL" ? 1 : 0);

  const normalizeValue = useCallback(
    (value: string) =>
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""),
    []
  );

  const [skillSearch, setSkillSearch] = useState("");

  const popularSkillItems = useMemo(() => popularSkills.slice(0, 10), [popularSkills]);
  const normalizedPopularSkills = useMemo<NormalizedSkill[]>(
    () =>
      popularSkillItems.map((skill) => ({
        ...skill,
        normalized: normalizeValue(skill.name),
      })),
    [popularSkillItems, normalizeValue]
  );

  const normalizedAllSkills = useMemo<NormalizedSkill[]>(
    () =>
      allSkills.map((skill) => ({
        ...skill,
        normalized: normalizeValue(skill.name),
      })),
    [allSkills, normalizeValue]
  );

  const filteredAllSkills = useMemo<NormalizedSkill[]>(() => {
    if (!skillSearch.trim()) {
      return normalizedAllSkills;
    }
    const term = normalizeValue(skillSearch);
    return normalizedAllSkills.filter((skill) => skill.normalized.includes(term));
  }, [normalizedAllSkills, skillSearch, normalizeValue]);
  const filteredPopularSkills = useMemo<NormalizedSkill[]>(() => {
    if (!skillSearch.trim()) {
      return normalizedPopularSkills;
    }
    const term = normalizeValue(skillSearch);
    return normalizedPopularSkills.filter((skill) => skill.normalized.includes(term));
  }, [normalizedPopularSkills, skillSearch, normalizeValue]);

  const alphabeticalGroups = useMemo<Array<[string, NormalizedSkill[]]>>(() => {
    const groups = new Map<string, NormalizedSkill[]>();
    filteredAllSkills.forEach((skill) => {
      const letter = skill.name.charAt(0).toUpperCase();
      if (!groups.has(letter)) {
        groups.set(letter, []);
      }
      groups.get(letter)?.push(skill);
    });
    return Array.from(groups.entries())
      .map(([letter, items]) => [
        letter,
        items.sort((a, b) => a.name.localeCompare(b.name)),
      ] as [string, NormalizedSkill[]])
      .sort(([a], [b]) => a.localeCompare(b));
  }, [filteredAllSkills]);

  const skillLookup = useMemo(() => {
    const map = new Map<number, SkillOption>();
    [...allSkills, ...popularSkills].forEach((skill) => {
      if (!map.has(skill.id)) {
        map.set(skill.id, skill);
      }
    });
    return map;
  }, [allSkills, popularSkills]);

  const interpretSkillMap = useMemo(
    () => new Map(interpretSkillLabels.map((s) => [s.id, s])),
    [interpretSkillLabels]
  );
  const interpretJurisdictionMap = useMemo(
    () => new Map(interpretJurisdictionLabels.map((j) => [j.id, j])),
    [interpretJurisdictionLabels]
  );
  const selectedSkills = useMemo(
    () =>
      selectedSkillIds.map((rawId) => {
        const id = Number(rawId);
        return skillLookup.get(id) ?? interpretSkillMap.get(id);
      }).filter((item): item is SkillOption => Boolean(item)),
    [selectedSkillIds, skillLookup, interpretSkillMap]
  );
  const selectedLocations = useMemo(
    () =>
      selectedJurisdictionIds.map((rawId) => {
        const id = Number(rawId);
        return jurisdictions.find((j) => j.id === id) ?? interpretJurisdictionMap.get(id);
      }).filter((item): item is JurisdictionOption => Boolean(item)),
    [jurisdictions, selectedJurisdictionIds, interpretJurisdictionMap]
  );
  const selectedCategories = useMemo(
    () =>
      selectedCategoryIds
        .map((rawId) => {
          const id = Number(rawId);
          return categories.find((c) => c.id === id);
        })
        .filter((item): item is CategoryOption => Boolean(item)),
    [categories, selectedCategoryIds]
  );
  const categoryFieldSummary = useMemo(() => {
    const n = selectedCategoryIds.length;
    if (n === 0) return undefined;
    if (n === 1) return selectedCategories[0]?.name ?? "1 categoría";
    return `${n} categorías`;
  }, [selectedCategories, selectedCategoryIds.length]);
  const validatedExploreTrue = validatedExplore?.true;
  const validatedExploreFalse = validatedExplore?.false;
  const validatedAppliedTrue = validatedApplied?.true;
  const validatedAppliedFalse = validatedApplied?.false;
  const typeLabelMap = useMemo(
    () => ({
      ALL: typeof typesExplore?.total === "number" ? `Todos (${typesExplore.total})` : "Todos",
      FEATURED: typeof featuredCount === "number" ? `Destacados (${featuredCount})` : "Destacados",
      PROFESSIONAL:
        typeof typesExplore?.person === "number"
          ? `Profesionales (${typesExplore.person})`
          : "Profesionales",
      COMPANY:
        typeof typesExplore?.company === "number"
          ? `Empresas (${typesExplore.company})`
          : "Empresas",
      WORKER: "Trabajadores",
    }),
    [typesExplore, featuredCount]
  );

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
    document.body.style.overflow = "";
    return undefined;
  }, [mobileOpen]);

  const handleSectionToggle = (section: SectionKey) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const handleClear = useCallback(() => {
    onResetType?.();
    onClearFilters();
  }, [onClearFilters, onResetType]);

  useEffect(() => {
    if (activeSection !== "skills" && activeSection !== "skills-mobile") {
      setSkillSearch("");
    }
  }, [activeSection]);

  const buildSummary = () => {
  const hasTypeFilter = type !== "ALL";
    const currentTypeLabel = typeOptions.find((option) => option.value === type)?.label;
  const hasFilters = filterCount > 0 || verificationFilter !== "ALL" || featured || hasTypeFilter;
    if (!hasFilters) {
      return <span className={styles.emptySummary}>Sin filtros activos</span>;
    }

    return (
      <div className={styles.summaryChips}>
        {hasTypeFilter && onResetType && currentTypeLabel && (
          <button type="button" className={styles.chip} onClick={onResetType}>
            {currentTypeLabel}
            <X size={12} />
          </button>
        )}
        {nameValue?.trim() && onSearchClear && (
          <button type="button" className={styles.chip} onClick={onSearchClear}>
            <Search size={14} />
            {nameValue.trim()}
            <X size={12} />
          </button>
        )}
        {verificationFilter === "TRUE" && (
          <button type="button" className={styles.chip} onClick={() => onVerificationChange("ALL")}>
            <ShieldCheck size={14} />
            {typeof validatedAppliedTrue === "number" ? `Verificados (${validatedAppliedTrue})` : "Verificados"}
            <X size={12} />
          </button>
        )}
        {verificationFilter === "FALSE" && (
          <button type="button" className={styles.chip} onClick={() => onVerificationChange("ALL")}>
            <ShieldCheck size={14} />
            {typeof validatedAppliedFalse === "number" ? `No verificados (${validatedAppliedFalse})` : "No verificados"}
            <X size={12} />
          </button>
        )}
        {featured && onToggleFeatured && (
          <button type="button" className={styles.chip} onClick={onToggleFeatured}>
            <Star size={14} />
            Destacados
            <X size={12} />
          </button>
        )}
        {selectedCategories.map((cat) => (
          <button key={cat.id} type="button" className={styles.chip} onClick={() => onCategoryToggle(cat.id)}>
            <Tags size={14} />
            {cat.name}
            <X size={12} />
          </button>
        ))}
        {selectedLocations.map((jur) => (
          <button key={jur.id} type="button" className={styles.chip} onClick={() => onJurisdictionToggle(jur.id)}>
            <MapPin size={14} />
            {jur.name}
            <X size={12} />
          </button>
        ))}
        {allCountrySelected && (
          <button type="button" className={styles.chip} onClick={onToggleAllCountry}>
            <MapPin size={14} />
            {typeof allCountryUsers === "number" ? `Todo el país (${allCountryUsers})` : "Todo el país"}
            <X size={12} />
          </button>
        )}
        {selectedSkills.map((skill) => (
          <button key={skill.id} type="button" className={styles.chip} onClick={() => onSkillToggle(skill.id)}>
            <Sparkles size={14} />
            {skill.name}
            <X size={12} />
          </button>
        ))}
      </div>
    );
  };

  const skillClick = (id: number, handler?: (id: number) => void) =>
    handler ? () => handler(id) : () => onSkillToggle(id);

  const renderTopSkills = (onSkillClickOverride?: (id: number) => void) => (
    <>
      <div className={styles.skillSearch}>
        <Search size={16} />
        <input
          type="text"
          value={skillSearch}
          onChange={(event) => setSkillSearch(event.target.value)}
          placeholder="Buscar especialidad"
          aria-label="Buscar especialidad"
        />
      </div>
      <motion.div
        className={styles.skillGrid}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
      >
        {filteredPopularSkills.map((skill) => (
          <button
            key={`${skill.id}-${skill.name}`}
            type="button"
            className={styles.skillPill}
            data-active={selectedSkillIds.includes(skill.id)}
            onClick={skillClick(skill.id, onSkillClickOverride)}
          >
            <span>{skill.name}</span>
            <span className={styles.skillCount}>{skill.users ?? 0}</span>
          </button>
        ))}
        {filteredPopularSkills.length === 0 && (
          <span className={styles.emptyState}>No se encontraron especialidades</span>
        )}
      </motion.div>
    </>
  );

  const renderAllSkills = (onSkillClickOverride?: (id: number) => void) => (
    <>
      <div className={styles.skillSearch}>
        <Search size={16} />
        <input
          type="text"
          value={skillSearch}
          onChange={(event) => setSkillSearch(event.target.value)}
          placeholder="Buscar especialidad"
          aria-label="Buscar especialidad"
        />
      </div>
      <motion.div
        className={styles.skillList}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
      >
        {filteredAllSkills.map((skill) => (
          <button
            key={`${skill.id}-${skill.name}`}
            type="button"
            className={styles.skillItem}
            data-active={selectedSkillIds.includes(skill.id)}
            onClick={skillClick(skill.id, onSkillClickOverride)}
          >
            <span>{skill.name}</span>
          </button>
        ))}
        {filteredAllSkills.length === 0 && (
          <span className={styles.emptyState}>No se encontraron especialidades</span>
        )}
      </motion.div>
    </>
  );

  const renderAlphabeticalSkills = (onSkillClickOverride?: (id: number) => void) => (
    <>
      <div className={styles.skillSearch}>
        <Search size={16} />
        <input
          type="text"
          value={skillSearch}
          onChange={(event) => setSkillSearch(event.target.value)}
          placeholder="Buscar especialidad"
          aria-label="Buscar especialidad"
        />
      </div>
      <motion.div
        className={styles.alphabeticalGroups}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
      >
        {alphabeticalGroups.map(([letter, items]) => (
          <div key={letter} className={styles.alphabeticalGroup}>
            <div className={styles.alphabeticalHeader}>
              <span>{letter}</span>
            </div>
            <div className={styles.alphabeticalItems}>
              {items.map((skill) => (
                <button
                  key={`${skill.id}-${skill.name}`}
                  type="button"
                  className={styles.skillItem}
                  data-active={selectedSkillIds.includes(skill.id)}
                  onClick={skillClick(skill.id, onSkillClickOverride)}
                >
                  <span>{skill.name}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
        {alphabeticalGroups.length === 0 && (
          <div className={styles.emptyState}>No hay especialidades disponibles</div>
        )}
      </motion.div>
    </>
  );

  const renderSkillsContent = (onSkillClickOverride?: (id: number) => void) => {
    switch (activeSkillView) {
      case "top":
        return renderTopSkills(onSkillClickOverride);
      case "all":
        return renderAllSkills(onSkillClickOverride);
      case "alphabetical":
        return renderAlphabeticalSkills(onSkillClickOverride);
      default:
        return null;
    }
  };

  const renderCategoriesContent = () => (
    <motion.section
      className={styles.skillPanel}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      <p className={styles.filterHint}>
        Agrupaciones amplias del perfil; podés elegir varias a la vez. Los conteos siguen el resto de filtros activos.
      </p>
      <motion.div
        className={styles.skillGrid}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.24, ease: "easeOut" }}
      >
        {categories.map((cat) => (
          <button
            key={`${cat.id}-${cat.name}`}
            type="button"
            className={styles.skillPill}
            data-active={selectedCategoryIds.includes(cat.id)}
            onClick={() => onCategoryToggle(cat.id)}
          >
            <span>{cat.name}</span>
            <span className={styles.skillCount}>{cat.users ?? 0}</span>
          </button>
        ))}
      </motion.div>
      {categories.length === 0 && <div className={styles.emptyState}>Sin categorías disponibles</div>}
    </motion.section>
  );

  const renderJurisdictionsContent = () => (
    <motion.div
      className={styles.locationPanel}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className={styles.locationList}>
        <p className={styles.filterHint}>
          Al filtrar por jurisdicción, también incluimos perfiles con cobertura en todo el país.
        </p>
        <button
          type="button"
          className={styles.locationItem}
          data-active={allCountrySelected}
          onClick={onToggleAllCountry}
        >
          <span>Todo el país</span>
          {typeof allCountryUsers === "number" && (
            <span className={styles.locationCount}>{allCountryUsers}</span>
          )}
        </button>
        {jurisdictions.map((jur) => (
          <button
            key={`${jur.id}-${jur.name}`}
            type="button"
            className={styles.locationItem}
            data-active={selectedJurisdictionIds.includes(jur.id)}
            onClick={() => onJurisdictionToggle(jur.id)}
          >
            <span>{jur.name}</span>
            {typeof jur.users === "number" && (
              <span className={styles.locationCount}>{jur.users}</span>
            )}
          </button>
        ))}
        {jurisdictionsLoaded && jurisdictions.length === 0 && <div className={styles.emptyState}>Sin ubicaciones disponibles</div>}
      </div>
    </motion.div>
  );

  return (
    <div className={styles.root}>
      <div className={styles.desktop}>
        <FilterPanel
          title="Filtros"
          filterCount={appliedFiltersCount}
          onReset={handleClear}
          advancedOpen={false}
          onToggleAdvanced={() => undefined}
          primary={
            <FilterSection>
              <FilterField>
                <FilterFieldButton
                  title="Tipo de perfil"
                  value={type === "ALL" ? undefined : typeLabelMap[type]}
                  placeholder="Todos"
                  onClick={() => handleSectionToggle("type")}
                  active={activeSection === "type"}
                />
                <Presence initial={false}>
                  {activeSection === "type" && (
                    <motion.section
                      className={styles.skillPanel}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <div className={styles.skillList}>
                        {typeOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={styles.skillItem}
                            data-active={type === option.value}
                            onClick={() => {
                              onTypeChange(option.value);
                              setActiveSection(null);
                            }}
                          >
                            <span>{typeLabelMap[option.value]}</span>
                          </button>
                        ))}
                      </div>
                    </motion.section>
                  )}
                </Presence>
              </FilterField>
              <FilterField>
                <FilterFieldButton
                  title="Categorías"
                  value={categoryFieldSummary}
                  placeholder="Todas"
                  onClick={() => handleSectionToggle("categories")}
                  active={activeSection === "categories"}
                />
                <Presence initial={false}>{activeSection === "categories" && renderCategoriesContent()}</Presence>
              </FilterField>
              <FilterField>
                <FilterFieldButton
                  title="Especialidades"
                  value={selectedSkills[0]?.name}
                  placeholder="Todas"
                  onClick={() => handleSectionToggle("skills")}
                  active={activeSection === "skills"}
                />
                <Presence initial={false}>
                  {activeSection === "skills" && (
                    <motion.section
                      className={styles.skillPanel}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <p className={styles.filterHint}>
                        Rubros concretos para acotar resultados (más específico que las categorías).
                      </p>
                      <nav className={styles.skillTabs}>
                        <button
                          type="button"
                          className={styles.skillTab}
                          data-active={activeSkillView === "top"}
                          onClick={() => setActiveSkillView("top")}
                        >
                          Populares
                        </button>
                        <button
                          type="button"
                          className={styles.skillTab}
                          data-active={activeSkillView === "all"}
                          onClick={() => setActiveSkillView("all")}
                        >
                          Todas
                        </button>
                        <button
                          type="button"
                          className={styles.skillTab}
                          data-active={activeSkillView === "alphabetical"}
                          onClick={() => setActiveSkillView("alphabetical")}
                        >
                          Por letra
                        </button>
                      </nav>
                      <Presence initial={false}>{renderSkillsContent()}</Presence>
                    </motion.section>
                  )}
                </Presence>
              </FilterField>
              <FilterField>
                <FilterFieldButton
                  title="Ubicación"
                  value={selectedLocations[0]?.name}
                  placeholder="Todo el país"
                  onClick={() => handleSectionToggle("jurisdictions")}
                  active={activeSection === "jurisdictions"}
                />
                <Presence initial={false}>{activeSection === "jurisdictions" && renderJurisdictionsContent()}</Presence>
              </FilterField>
              <FilterField>
                <FilterFieldButton
                  title="Verificación"
                  value={
                    verificationFilter === "TRUE"
                      ? "Solo verificados"
                      : verificationFilter === "FALSE"
                        ? "No verificados"
                        : undefined
                  }
                  placeholder="Todos"
                  onClick={() => handleSectionToggle("verified")}
                  active={activeSection === "verified"}
                />
                <Presence initial={false}>
                  {activeSection === "verified" && (
                    <motion.section
                      className={styles.skillPanel}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      <div className={styles.skillList}>
                        <p className={styles.filterHint}>
                          Los conteos de opciones son orientativos y pueden variar según filtros aplicados.
                        </p>
                        <button
                          type="button"
                          className={styles.skillItem}
                          data-active={verificationFilter === "ALL"}
                          onClick={() => {
                            if (verificationFilter !== "ALL") onVerificationChange("ALL");
                            setActiveSection(null);
                          }}
                        >
                          <span>
                            {typeof validatedExploreTrue === "number" && typeof validatedExploreFalse === "number"
                              ? `Todos (${validatedExploreTrue + validatedExploreFalse})`
                              : "Todos"}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.skillItem}
                          data-active={verificationFilter === "TRUE"}
                          onClick={() => {
                            if (verificationFilter !== "TRUE") onVerificationChange("TRUE");
                            setActiveSection(null);
                          }}
                        >
                          <span>
                            {typeof validatedExploreTrue === "number"
                              ? `Solo verificados (${validatedExploreTrue})`
                              : "Solo verificados"}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.skillItem}
                          data-active={verificationFilter === "FALSE"}
                          onClick={() => {
                            if (verificationFilter !== "FALSE") onVerificationChange("FALSE");
                            setActiveSection(null);
                          }}
                        >
                          <span>
                            {typeof validatedExploreFalse === "number"
                              ? `No verificados (${validatedExploreFalse})`
                              : "No verificados"}
                          </span>
                        </button>
                      </div>
                    </motion.section>
                  )}
                </Presence>
              </FilterField>
            </FilterSection>
          }
          summary={buildSummary()}
        />
      </div>
      <div className={styles.mobile}>
        <div className={styles.mobileTriggerBar}>
          <button type="button" className={styles.mobileTrigger} onClick={() => setMobileOpen(true)}>
            <Filter size={18} />
            Filtros
            {appliedFiltersCount > 0 && <span className={styles.mobileBadge}>{appliedFiltersCount}</span>}
          </button>
          <div className={styles.mobileResults}>
            <span className={styles.mobileResultsCount}>{totalCount.toLocaleString("es-AR")}</span>
            <span className={styles.mobileResultsText}>{totalCount === 1 ? "resultado" : "resultados"}</span>
          </div>
        </div>
        <Presence initial={false}>
          {mobileOpen && (
            <motion.div
              className={styles.mobileOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                ref={mobileRef}
                className={styles.mobileSheet}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 26, stiffness: 260 }}
              >
                <header className={styles.mobileHeader}>
                  <div>
                    <h3>Filtros</h3>
                    <p>{appliedFiltersCount === 0 ? "Sin filtros activos" : `${appliedFiltersCount} filtros activos`}</p>
                  </div>
                  <button
                    type="button"
                    className={styles.mobileClose}
                    onClick={() => {
                      setMobileOpen(false);
                      setActiveSection(null);
                    }}
                  >
                    <X size={20} />
                  </button>
                </header>
                <div className={styles.mobileContent}>
                  {appliedFiltersCount > 0 && (
                    <div className={styles.mobileSummaryChips}>
                      {buildSummary()}
                    </div>
                  )}
                  <FilterFieldButton
                    title="Tipo de perfil"
                    value={typeLabelMap[type]}
                    onClick={() => handleSectionToggle("type-mobile")}
                    active={activeSection === "type-mobile"}
                  />
                  <Presence initial={false}>
                    {activeSection === "type-mobile" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ maxHeight: 0, opacity: 0 }}
                        animate={{
                          maxHeight: "24rem",
                          opacity: 1,
                          transition: { duration: 0.24, ease: "easeOut" },
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          transition: { duration: 0.18, ease: "easeIn" },
                        }}
                      >
                        {typeOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={styles.dropdownButton}
                            data-active={type === option.value}
                            onClick={() => {
                              onTypeChange(option.value);
                              setActiveSection(null);
                              setMobileOpen(false);
                            }}
                          >
                            <span>{typeLabelMap[option.value]}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title="Categorías"
                    value={categoryFieldSummary ?? "Todas"}
                    onClick={() => handleSectionToggle("categories-mobile")}
                    active={activeSection === "categories-mobile"}
                  />
                  <Presence initial={false}>
                    {activeSection === "categories-mobile" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ maxHeight: 0, opacity: 0 }}
                        animate={{
                          maxHeight: "40rem",
                          opacity: 1,
                          transition: { duration: 0.24, ease: "easeOut" },
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          transition: { duration: 0.18, ease: "easeIn" },
                        }}
                      >
                        <div className={styles.mobileDropdownList}>
                          <p className={styles.filterHint}>
                            Agrupaciones amplias; podés marcar varias. Los conteos siguen el resto de filtros activos.
                          </p>
                          <div className={styles.skillGrid}>
                            {categories.map((cat) => (
                              <button
                                key={cat.id}
                                type="button"
                                className={styles.skillPill}
                                data-active={selectedCategoryIds.includes(cat.id)}
                                onClick={() => onCategoryToggle(cat.id)}
                              >
                                <span>{cat.name}</span>
                                <span className={styles.skillCount}>{cat.users ?? 0}</span>
                              </button>
                            ))}
                          </div>
                          {categories.length === 0 && (
                            <div className={styles.emptyState}>Sin categorías disponibles</div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title="Especialidades"
                    value={selectedSkills[0]?.name ?? "Todas"}
                    onClick={() => handleSectionToggle("skills-mobile")}
                    active={activeSection === "skills-mobile"}
                  />
                  <Presence initial={false}>
                    {activeSection === "skills-mobile" && (
                      <motion.div
                        className={styles.mobileSkills}
                        initial={{ maxHeight: 0, opacity: 0 }}
                        animate={{
                          maxHeight: "40rem",
                          opacity: 1,
                          transition: { duration: 0.24, ease: "easeOut" },
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          transition: { duration: 0.18, ease: "easeIn" },
                        }}
                      >
                        <p className={styles.filterHint}>
                          Rubros concretos para acotar (más específico que las categorías).
                        </p>
                        <nav className={styles.skillTabs}>
                          <button
                            type="button"
                            className={styles.skillTab}
                            data-active={activeSkillView === "top"}
                            onClick={() => setActiveSkillView("top")}
                          >
                            Populares
                          </button>
                          <button
                            type="button"
                            className={styles.skillTab}
                            data-active={activeSkillView === "all"}
                            onClick={() => setActiveSkillView("all")}
                          >
                            Todas
                          </button>
                          <button
                            type="button"
                            className={styles.skillTab}
                            data-active={activeSkillView === "alphabetical"}
                            onClick={() => setActiveSkillView("alphabetical")}
                          >
                            Por letra
                          </button>
                        </nav>
                        {renderSkillsContent((id) => {
                          onSkillToggle(id);
                          setActiveSection(null);
                          setMobileOpen(false);
                        })}
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title="Ubicación"
                  value={
                    selectedLocations.length > 0
                      ? selectedLocations[0].name
                      : allCountrySelected
                        ? "Todo el país"
                        : "Todo el país"
                  }
                    onClick={() => handleSectionToggle("jurisdictions-mobile")}
                    active={activeSection === "jurisdictions-mobile"}
                  />
                  <Presence initial={false}>
                    {activeSection === "jurisdictions-mobile" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ maxHeight: 0, opacity: 0 }}
                        animate={{
                          maxHeight: "24rem",
                          opacity: 1,
                          transition: { duration: 0.24, ease: "easeOut" },
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          transition: { duration: 0.18, ease: "easeIn" },
                        }}
                      >
                        <div className={styles.mobileDropdownList}>
                        <p className={styles.filterHint}>
                          Al filtrar por jurisdicción, también incluimos perfiles con cobertura en todo el país.
                        </p>
                        <button
                          type="button"
                          className={styles.dropdownButton}
                          data-active={allCountrySelected}
                          onClick={() => {
                            onToggleAllCountry();
                            setActiveSection(null);
                            setMobileOpen(false);
                          }}
                        >
                          <span>Todo el país</span>
                          {typeof allCountryUsers === "number" && (
                            <span className={styles.locationCount}>{allCountryUsers}</span>
                          )}
                        </button>
                        {jurisdictions.map((jur) => (
                          <button
                            key={jur.id}
                            type="button"
                            className={styles.dropdownButton}
                            data-active={selectedJurisdictionIds.includes(jur.id)}
                            onClick={() => {
                              onJurisdictionToggle(jur.id);
                              setActiveSection(null);
                              setMobileOpen(false);
                            }}
                          >
                            <span>{jur.name}</span>
                            {typeof jur.users === "number" && (
                              <span className={styles.locationCount}>{jur.users}</span>
                            )}
                          </button>
                        ))}
                        {jurisdictionsLoaded && jurisdictions.length === 0 && <div className={styles.emptyState}>Sin ubicaciones disponibles</div>}
                        </div>
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title="Verificación"
                    value={
                      verificationFilter === "TRUE"
                        ? "Solo verificados"
                        : verificationFilter === "FALSE"
                          ? "No verificados"
                          : "Todos"
                    }
                    onClick={() => handleSectionToggle("verified-mobile")}
                    active={activeSection === "verified-mobile"}
                  />
                  <Presence initial={false}>
                    {activeSection === "verified-mobile" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ maxHeight: 0, opacity: 0 }}
                        animate={{
                          maxHeight: "24rem",
                          opacity: 1,
                          transition: { duration: 0.24, ease: "easeOut" },
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          transition: { duration: 0.18, ease: "easeIn" },
                        }}
                      >
                        <p className={styles.filterHint}>
                          Los conteos de opciones son orientativos y pueden variar según filtros aplicados.
                        </p>
                        <button
                          type="button"
                          className={styles.dropdownButton}
                          data-active={verificationFilter === "ALL"}
                          onClick={() => {
                            if (verificationFilter !== "ALL") onVerificationChange("ALL");
                            setActiveSection(null);
                            setMobileOpen(false);
                          }}
                        >
                          <span>
                            {typeof validatedExploreTrue === "number" && typeof validatedExploreFalse === "number"
                              ? `Todos (${validatedExploreTrue + validatedExploreFalse})`
                              : "Todos"}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.dropdownButton}
                          data-active={verificationFilter === "TRUE"}
                          onClick={() => {
                            if (verificationFilter !== "TRUE") onVerificationChange("TRUE");
                            setActiveSection(null);
                            setMobileOpen(false);
                          }}
                        >
                          <span>
                            {typeof validatedExploreTrue === "number"
                              ? `Solo verificados (${validatedExploreTrue})`
                              : "Solo verificados"}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.dropdownButton}
                          data-active={verificationFilter === "FALSE"}
                          onClick={() => {
                            if (verificationFilter !== "FALSE") onVerificationChange("FALSE");
                            setActiveSection(null);
                            setMobileOpen(false);
                          }}
                        >
                          <span>
                            {typeof validatedExploreFalse === "number"
                              ? `No verificados (${validatedExploreFalse})`
                              : "No verificados"}
                          </span>
                        </button>
                      </motion.div>
                    )}
                  </Presence>
                </div>
                <footer className={styles.mobileFooter}>
                  <button
                    type="button"
                    className={styles.mobileReset}
                    onClick={handleClear}
                  >
                    Limpiar filtros
                  </button>
                </footer>
              </motion.div>
            </motion.div>
          )}
        </Presence>
      </div>
    </div>
  );
};

export default PeopleSearchFilters;
